import { makeAutoObservable } from "mobx";
import type { RootStore, ServiceStore } from "@intrasync/stores";
import { ChartOfAccount, AccountType } from "../types";

// Account form data interface - matches glAccountData from api-types
export interface AccountFormData {
  id?: string;
  accountNumber: string;
  accountName: string;
  accountType: string;
  category?: string | null;
  parentAccountId?: string | null;
  isActive: boolean;
  isBankAccount: boolean;
  isCreditCard: boolean;
  description?: string | null;
  balance?: number | null;
}

class ChartOfAccountsStore {
  rootStore: RootStore;

  // Observables - Only UI state
  selectedAccountId: string | null = null;
  formData: AccountFormData;
  originalFormData: AccountFormData | null = null;
  isEditMode = false;
  selectedAccountIds: string[] = [];
  filterType: "all" | AccountType = "all";
  filterStatus: "all" | "active" | "inactive" = "active";
  searchQuery = "";
  expandedNodes: string[] = [];
  isLoading = false;
  mergeTargetAccountId: string | null = null;
  printIncludeBalances = true;
  deleteConfirmStep: 1 | 2 = 1;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    this.formData = this.getInitialFormData();

    makeAutoObservable(this, {
      rootStore: false,
    });
  }

  // Actions
  setFilterType = (type: "all" | AccountType) => {
    this.filterType = type;
  };

  setFilterStatus = (status: "all" | "active" | "inactive") => {
    this.filterStatus = status;
  };

  setSearchQuery = (query: string) => {
    this.searchQuery = query;
  };

  toggleNodeExpansion = (accountId: string) => {
    if (this.expandedNodes.includes(accountId)) {
      this.expandedNodes = this.expandedNodes.filter((id) => id !== accountId);
    } else {
      this.expandedNodes = [...this.expandedNodes, accountId];
    }
  };

  expandAll = () => {
    // Only expand accounts that have children (groups)
    this.expandedNodes = this.accountTree
      .filter((account) => this.hasChildren(account.id))
      .map((account) => String(account.id));
  };

  collapseAll = () => {
    this.expandedNodes = [];
  };

  openCreateForm = (parentAccountId?: string) => {
    this.formData = this.getInitialFormData();
    if (parentAccountId) {
      this.formData.parentAccountId = parentAccountId;
      // Inherit parent account type
      const parentAccount = this.accounts.find(
        (a) => a.id === parentAccountId
      );
      if (parentAccount) {
        this.formData.accountType = parentAccount.accountType;
      }
    }
    this.isEditMode = false;
    this.selectedAccountId = null;
  };

  selectAccount = (accountId: string) => {
    this.selectedAccountId = accountId;
  };

  openEditForm = (accountId: string) => {
    const account = this.chartOfAccountsStore?.entities.find(
      (a: any) => String(a.id) === accountId
    ) as unknown as ChartOfAccount;
    if (!account) return;

    this.formData = {
      id: String(account.id),
      accountNumber: account.accountNumber,
      accountName: account.accountName,
      accountType: account.accountType,
      category: account.category,
      parentAccountId: account.parentAccountId,
      isActive: account.isActive,
      isBankAccount: account.isBankAccount ?? false,
      isCreditCard: account.isCreditCard ?? false,
      description: account.description || "",
      balance: account.balance,
    };
    this.originalFormData = { ...this.formData };
    this.isEditMode = true;
    this.selectedAccountId = accountId;
  };

  closeForm = () => {
    this.isEditMode = false;
    this.selectedAccountId = null;
    this.formData = this.getInitialFormData();
    this.originalFormData = null;
  };

  get hasFormChanges(): boolean {
    if (!this.originalFormData) return true;
    const orig = this.originalFormData;
    const curr = this.formData;
    return (
      orig.accountNumber !== curr.accountNumber ||
      orig.accountName !== curr.accountName ||
      orig.accountType !== curr.accountType ||
      orig.category !== curr.category ||
      orig.parentAccountId !== curr.parentAccountId ||
      orig.isActive !== curr.isActive ||
      orig.isBankAccount !== curr.isBankAccount ||
      orig.isCreditCard !== curr.isCreditCard ||
      orig.description !== curr.description ||
      orig.balance !== curr.balance
    );
  }

  updateFormData = (
    field: keyof AccountFormData,
    value: string | number | boolean
  ) => {
    this.formData = { ...this.formData, [field]: value };
  };

  saveAccount = async () => {
    try {
      const data = {
        accountNumber: this.formData.accountNumber,
        accountName: this.formData.accountName,
        accountType: this.formData.accountType,
        category: this.formData.category,
        parentAccountId: this.formData.parentAccountId,
        isActive: this.formData.isActive,
        isBankAccount: this.formData.isBankAccount,
        isCreditCard: this.formData.isCreditCard,
        description: this.formData.description,
        balance: this.formData.balance,
      };

      if (this.isEditMode && this.formData.id) {
        await this.chartOfAccountsStore?.patch(
          this.formData.id,
          data as unknown as Record<string, unknown>
        );
      } else {
        await this.chartOfAccountsStore?.create(
          data as unknown as Record<string, unknown>
        );
      }

      this.closeForm();
    } catch (error) {
      throw error;
    }
  };

  printAccounts = () => {
    const ACCOUNT_TYPE_LABELS: Record<AccountType, string> = {
      [AccountType.CURRENT_ASSET]: "Current Assets",
      [AccountType.FIXED_ASSET]: "Fixed Assets",
      [AccountType.OTHER_ASSET]: "Other Assets",
      [AccountType.CURRENT_LIABILITY]: "Current Liabilities",
      [AccountType.LONG_TERM_LIABILITY]: "Long-Term Liabilities",
      [AccountType.EQUITY]: "Equity",
      [AccountType.RETAINED_EARNINGS]: "Retained Earnings",
      [AccountType.REVENUE]: "Revenue",
      [AccountType.COST_OF_GOODS_SOLD]: "Cost of Goods Sold",
      [AccountType.EXPENSE]: "Expenses",
      [AccountType.OTHER_INCOME_EXPENSE]: "Other Income / Expenses",
    };

    const TYPE_ORDER: AccountType[] = [
      AccountType.CURRENT_ASSET,
      AccountType.FIXED_ASSET,
      AccountType.OTHER_ASSET,
      AccountType.CURRENT_LIABILITY,
      AccountType.LONG_TERM_LIABILITY,
      AccountType.EQUITY,
      AccountType.RETAINED_EARNINGS,
      AccountType.REVENUE,
      AccountType.COST_OF_GOODS_SOLD,
      AccountType.EXPENSE,
      AccountType.OTHER_INCOME_EXPENSE,
    ];

    const formatCurrency = (amount: number) =>
      new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(amount);

    const includeBalances = this.printIncludeBalances;

    const renderRows = (account: ChartOfAccount, level: number): string => {
      const indentPx = 12 + level * 20;
      const inactive = !account.isActive
        ? ' <span style="color:#999;font-style:italic;">(Inactive)</span>'
        : "";
      const name = account.accountName.toLowerCase().trim();
      const desc = account.description?.toLowerCase().trim();
      const descSuffix =
        desc && desc !== name
          ? ` <span style="color:#666;font-weight:normal;"> — ${account.description}</span>`
          : "";
      const children = this.getChildAccounts(account.id);
      const balanceCell = includeBalances
        ? `<td style="padding:3px 8px;text-align:right;border-bottom:1px solid #e0e0e0;white-space:nowrap;">${formatCurrency(account.balance || 0)}</td>`
        : "";
      let html = `
        <tr>
          <td style="padding:3px 8px;padding-left:${indentPx}px;border-bottom:1px solid #e0e0e0;white-space:nowrap;">
            <strong>${account.accountNumber}</strong>
          </td>
          <td style="padding:3px 8px;border-bottom:1px solid #e0e0e0;">
            ${account.accountName}${descSuffix}${inactive}
          </td>
          ${balanceCell}
        </tr>`;
      children.forEach((child) => { html += renderRows(child, level + 1); });
      return html;
    };

    const byType = new Map<AccountType, ChartOfAccount[]>();
    TYPE_ORDER.forEach((type) => byType.set(type, []));
    this.accountTree.forEach((account) => {
      const type = account.accountType as AccountType;
      if (byType.has(type)) byType.get(type)!.push(account);
    });

    let firstSection = true;
    let sectionsHtml = "";
    TYPE_ORDER.forEach((type) => {
      const accounts = byType.get(type) || [];
      if (accounts.length === 0) return;
      const topBorder = firstSection ? "" : "border-top:12px solid #f5f5f5;";
      firstSection = false;
      sectionsHtml += `
        <tr>
          <td colspan="${includeBalances ? 3 : 2}" style="padding:10px 8px 5px;font-size:13px;font-weight:bold;
            letter-spacing:0.4px;border-bottom:2px solid #333;${topBorder}">
            ${ACCOUNT_TYPE_LABELS[type]}
          </td>
        </tr>
        ${accounts.map((a) => renderRows(a, 0)).join("")}`;
    });

    const filterParts: string[] = [];
    if (this.filterType !== "all") {
      filterParts.push(`Type: ${ACCOUNT_TYPE_LABELS[this.filterType as AccountType]}`);
    }
    if (this.filterStatus !== "all") {
      filterParts.push(`Status: ${this.filterStatus === "active" ? "Active Only" : "Inactive Only"}`);
    }
    const filterNote = filterParts.length > 0 ? filterParts.join(" \u2022 ") : "All Accounts";
    const printDate = new Date().toLocaleDateString("en-US", {
      year: "numeric", month: "long", day: "numeric",
    });

    const html = `<!DOCTYPE html><html><head><title>Chart of Accounts</title>
      <style>
        @page { margin: 0.75in 0.75in 0.75in 1.5in; }
        body { font-family: Arial, sans-serif; font-size: 12px; color: #000; margin: 0; padding: 0; }
        h1 { font-size: 20px; margin: 0 0 4px 0; }
        .meta { font-size: 11px; color: #444; padding-bottom: 10px; margin-bottom: 14px; border-bottom: 2px solid #000; }
        table { width: 100%; border-collapse: collapse; }
        thead th { background: #f0f0f0; padding: 5px 8px; text-align: left; font-size: 11px;
          text-transform: uppercase; letter-spacing: 0.5px; border-bottom: 2px solid #333; }
        thead th:last-child { text-align: right; }
        @media print { body { print-color-adjust: exact; -webkit-print-color-adjust: exact; } }
      </style>
      </head><body>
      <h1>Chart of Accounts</h1>
      <div class="meta">
        <div>Apex Group</div>
        <div>Printed: ${printDate}</div>
        <div>Showing: ${filterNote}</div>
      </div>
      <table>
        <thead>
          <tr>
            <th style="width:110px;">Account #</th>
            <th>Account Name</th>
            ${includeBalances ? '<th style="width:110px;text-align:right;">Balance</th>' : ""}
          </tr>
        </thead>
        <tbody>${sectionsHtml}</tbody>
      </table>
      </body></html>`;

    const win = window.open("", "_blank", "width=900,height=700");
    if (win) {
      win.document.write(html);
      win.document.close();
      win.focus();
      win.print();
    }
  };

  setPrintIncludeBalances = (value: boolean) => {
    this.printIncludeBalances = value;
  };

  setMergeTargetAccountId = (id: string | null) => {
    this.mergeTargetAccountId = id;
  };

  advanceDeleteConfirmStep = () => {
    this.deleteConfirmStep = 2;
  };

  resetDeleteConfirmStep = () => {
    this.deleteConfirmStep = 1;
  };

  mergeAccounts = async () => {
    const sourceId = this.selectedAccountId;
    const targetId = this.mergeTargetAccountId;
    if (!sourceId || !targetId) return;

    try {
      this.isLoading = true;
      // Deactivate the source account and record the merge target.
      // Backend is responsible for re-pointing transactions from source → target.
      await this.chartOfAccountsStore?.patch(sourceId, {
        isActive: false,
        mergedIntoAccountId: targetId,
      } as unknown as Record<string, unknown>);

      this.mergeTargetAccountId = null;
      this.selectedAccountId = null;
    } finally {
      this.isLoading = false;
    }
  };

  get mergeSourceAccount(): ChartOfAccount | null {
    return this.selectedAccountId
      ? (this.accounts.find((a) => String(a.id) === this.selectedAccountId) ?? null)
      : null;
  }

  get mergeTargetAccount(): ChartOfAccount | null {
    return this.mergeTargetAccountId
      ? (this.accounts.find((a) => String(a.id) === this.mergeTargetAccountId) ?? null)
      : null;
  }

  get mergeableAccounts(): ChartOfAccount[] {
    return this.accounts.filter(
      (a) => String(a.id) !== this.selectedAccountId && a.isActive
    );
  }

  navigateToGeneralLedger = (accountId: string) => {
    const glStore = (this.rootStore as any).generalLedgerStore;
    if (glStore?.setSelectedAccounts) {
      glStore.setSelectedAccounts([accountId]);
    }
    this.rootStore.routerStore?.goTo("GeneralLedger");
  };

  deleteAccount = async (accountId: string) => {
    try {
      // Check if account has children
      const hasChildren = this.accounts.some(
        (a) => a.parentAccountId === accountId
      );
      if (hasChildren) {
        throw new Error(
          "Cannot delete account with child accounts. Please delete or reassign child accounts first."
        );
      }

      await this.chartOfAccountsStore?.remove(accountId);
    } catch (error) {
      throw error;
    }
  };

  toggleAccountStatus = async (accountId: string) => {
    try {
      const account = this.accounts.find((a) => String(a.id) === accountId);
      if (!account) return;

      await this.chartOfAccountsStore?.patch(accountId, {
        isActive: !account.isActive,
      } as unknown as Record<string, unknown>);
    } catch (error) {
      throw error;
    }
  };

  loadAccounts = async () => {
    try {
      this.isLoading = true;
      // includeInactive: true bypasses the backend's default isActive: true hook
      // so admins can see and manage inactive accounts in the COA view.
      await this.chartOfAccountsStore?.find({ query: { includeInactive: true } } as any);
    } catch (error) {
      throw error;
    } finally {
      this.isLoading = false;
    }
  };

  private getInitialFormData(): AccountFormData {
    return {
      accountNumber: "",
      accountName: "",
      accountType: AccountType.CURRENT_ASSET,
      isActive: true,
      isBankAccount: false,
      isCreditCard: false,
      description: "",
      category: null,
      parentAccountId: null,
      balance: null,
    };
  }

  // Computed values
  get isCOAAdmin(): boolean {
    const auth = (this.rootStore as any).authStore;
    if (!auth) return false;
    const roles: string[] = auth.currentUser?.roles ?? (auth.currentUser?.role ? [auth.currentUser.role] : []);
    return roles.includes("accounting_admin") || auth.isAdminUser === true;
  }

  get chartOfAccountsStore(): ServiceStore | undefined {
    // Service store is named "chart-of-accounts" -> "chartOfAccountsStore"
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (this.rootStore as any).chartOfAccountsStore as ServiceStore | undefined;
  }

  get accounts(): ChartOfAccount[] {
    return (
      (this.chartOfAccountsStore?.entities as unknown as ChartOfAccount[]) || []
    );
  }

  get selectedAccount(): ChartOfAccount | null {
    return this.selectedAccountId
      ? ((this.chartOfAccountsStore?.entities.find(
          (a: any) => String(a.id) === this.selectedAccountId
        ) as unknown as ChartOfAccount) || null)
      : null;
  }


  get filteredAccounts(): ChartOfAccount[] {
    let filtered = this.accounts;

    // Filter by account type
    if (this.filterType !== "all") {
      filtered = filtered.filter(
        (account) => account.accountType === this.filterType
      );
    }

    // Filter by active status
    if (this.filterStatus !== "all") {
      const isActive = this.filterStatus === "active";
      filtered = filtered.filter((account) => account.isActive === isActive);
    }

    // Filter by search query
    if (this.searchQuery) {
      const query = this.searchQuery.toLowerCase();
      filtered = filtered.filter(
        (account) =>
          account.accountNumber.toLowerCase().includes(query) ||
          account.accountName.toLowerCase().includes(query) ||
          account.description?.toLowerCase().includes(query)
      );
    }

    return filtered;
  }

  /**
   * Extract base account number for grouping.
   * Examples: "1020.001" -> "1020", "1020.5" -> "1020", "1020" -> "1020"
   */
  private getBaseAccountNumber(accountNumber: string): string {
    if (!accountNumber) return "";
    const dotIndex = accountNumber.indexOf(".");
    return dotIndex > 0 ? accountNumber.substring(0, dotIndex) : accountNumber;
  }


  /**
   * Build hierarchical tree structure by inferring hierarchy from account numbers.
   * Groups accounts by their base number (before the decimal point).
   */
  get accountTree(): ChartOfAccount[] {
    // Group accounts by base number
    const accountsByBase = new Map<string, ChartOfAccount[]>();

    this.filteredAccounts.forEach((account) => {
      const baseNum = this.getBaseAccountNumber(account.accountNumber);
      if (!accountsByBase.has(baseNum)) {
        accountsByBase.set(baseNum, []);
      }
      accountsByBase.get(baseNum)!.push(account);
    });

    // Build tree: return accounts grouped by base number
    // Only return the first account in each group as the "parent" for display
    const rootAccounts: ChartOfAccount[] = [];

    accountsByBase.forEach((accounts, _baseNum) => {
      if (accounts.length === 1) {
        // Single account - show at root level
        rootAccounts.push(accounts[0]);
      } else {
        // Multiple accounts with same base - use first one as group header
        // Sort by account number to get consistent ordering
        const sorted = accounts.sort((a, b) =>
          (a.accountNumber || "").localeCompare(b.accountNumber || "")
        );
        rootAccounts.push(sorted[0]);
      }
    });

    // Sort root accounts by account number
    return rootAccounts.sort((a, b) =>
      (a.accountNumber || "").localeCompare(b.accountNumber || "")
    );
  }

  /**
   * Get child accounts (sibling accounts with same base number).
   * @param accountId - The ID of the "parent" account
   */
  getChildAccounts = (accountId: string): ChartOfAccount[] => {
    const parentAccount = this.filteredAccounts.find((a) => a.id === accountId);
    if (!parentAccount) return [];

    const baseNum = this.getBaseAccountNumber(parentAccount.accountNumber);

    // Return all accounts with the same base number, except the parent itself
    return this.filteredAccounts
      .filter((account) => {
        const accountBase = this.getBaseAccountNumber(account.accountNumber);
        return accountBase === baseNum && account.id !== accountId;
      })
      .sort((a, b) => (a.accountNumber || "").localeCompare(b.accountNumber || ""));
  };

  /**
   * Check if account has children (other accounts with same base number).
   * Only returns true if this account is the FIRST in its group (the group header).
   */
  hasChildren = (accountId: string): boolean => {
    const account = this.accounts.find((a) => a.id === accountId);
    if (!account) return false;

    const baseNum = this.getBaseAccountNumber(account.accountNumber);

    // Get all accounts with same base number
    const siblings = this.filteredAccounts.filter((a) => {
      const aBase = this.getBaseAccountNumber(a.accountNumber);
      return aBase === baseNum;
    });

    // Only 1 account in group - no children
    if (siblings.length <= 1) return false;

    // Sort siblings to find the first one (group header)
    const sorted = [...siblings].sort((a, b) =>
      (a.accountNumber || "").localeCompare(b.accountNumber || "")
    );

    // Only the first account (group header) can have children
    // Child accounts should NOT be expandable to prevent infinite recursion
    return sorted[0].id === accountId;
  };

  // Get account path (breadcrumb)
  getAccountPath = (accountId: string): ChartOfAccount[] => {
    const path: ChartOfAccount[] = [];
    let currentAccount = this.accounts.find((a) => String(a.id) === accountId);

    while (currentAccount) {
      path.unshift(currentAccount);
      currentAccount = currentAccount.parentAccountId
        ? this.accounts.find((a) => a.id === currentAccount!.parentAccountId)
        : undefined;
    }

    return path;
  };

  // Summary by account type
  get accountSummary() {
    const summary = {
      [AccountType.CURRENT_ASSET]: { count: 0, balance: 0 },
      [AccountType.FIXED_ASSET]: { count: 0, balance: 0 },
      [AccountType.OTHER_ASSET]: { count: 0, balance: 0 },
      [AccountType.CURRENT_LIABILITY]: { count: 0, balance: 0 },
      [AccountType.LONG_TERM_LIABILITY]: { count: 0, balance: 0 },
      [AccountType.EQUITY]: { count: 0, balance: 0 },
      [AccountType.RETAINED_EARNINGS]: { count: 0, balance: 0 },
      [AccountType.REVENUE]: { count: 0, balance: 0 },
      [AccountType.COST_OF_GOODS_SOLD]: { count: 0, balance: 0 },
      [AccountType.EXPENSE]: { count: 0, balance: 0 },
      [AccountType.OTHER_INCOME_EXPENSE]: { count: 0, balance: 0 },
    };

    this.accounts.forEach((account) => {
      if (account.isActive && summary[account.accountType as AccountType]) {
        summary[account.accountType as AccountType].count++;
        summary[account.accountType as AccountType].balance += account.balance || 0;
      }
    });

    return summary;
  }

  get totalActiveAccounts(): number {
    return this.accounts.filter((a) => a.isActive).length;
  }

  get totalInactiveAccounts(): number {
    return this.accounts.filter((a) => !a.isActive).length;
  }

  get isAllExpanded(): boolean {
    const expandableIds = this.accountTree
      .filter((account) => this.hasChildren(account.id))
      .map((account) => String(account.id));
    return expandableIds.length > 0 && expandableIds.every((id) => this.expandedNodes.includes(id));
  }

  get isAllCollapsed(): boolean {
    return this.expandedNodes.length === 0;
  }
}

export default ChartOfAccountsStore;
